import React, { useState } from 'react';

import classNames from 'classnames';

import { QaIdentifier, useQa } from 'hooks/useQa';

import styles from './Input.module.scss';

export interface InputOptions {
    name: string;
    id: string;
    type: 'text' | 'password' | 'tel' | 'search' | 'url' | 'number' | 'email';
    value: string;
    placeholder?: string;
    icon?: React.ReactNode;
    autoComplete?: string;
    maxLength?: number;
}

export interface InputProps {
    /** Object with settings for input */
    input: InputOptions;
    /** Object with settings for label */
    label?: {
        title: string;
    };
    withClearButton?: boolean;
    customClass?: {
        InputWrapper?: string;
        InputField?: string;
        Input?: string;
        Label?: string;
        Icon?: string;
    };
    hasError?: boolean;
    errorText?: string;
    isDisabled?: boolean;
    onChange: (value: string) => void;
    onClear?: () => void;
    onKeyUp?: (e: React.KeyboardEvent<HTMLElement>) => void;
    onClick?: () => void;
    qaId?: QaIdentifier;
}

const Input: React.FC<React.PropsWithChildren<InputProps>> = ({
    input,
    label,
    withClearButton = false,
    hasError = false,
    errorText = '',
    isDisabled = false,
    children,
    customClass,
    onChange,
    onClear,
    onKeyUp,
    onClick,
    qaId,
}) => {
    const [clearVisible, setClearVisible] = useState(false);
    const qaAttr = useQa(qaId);
    const clearInput = () => {
        if (clearVisible) {
            onChange('');
            setClearVisible(false);
            onClear?.();
        }
    };
    const showClear = () => {
        !!input.value && setClearVisible(true);
    };
    const hideClear = () => {
        !input.value && setClearVisible(false);
    };

    const isSearchType = input.type === 'search';
    const updateInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
        withClearButton && !!input.value && setClearVisible(true);
    };
    return (
        <div
            className={classNames(
                styles.InputWrapper,
                customClass?.InputWrapper
            )}
            onBlur={hideClear}
            onClick={onClick}
            {...qaAttr}
        >
            {label && (
                <label
                    className={customClass?.Label}
                    htmlFor={input.id}
                    title={label.title}
                >
                    {children}
                </label>
            )}
            <div
                className={classNames(
                    styles.InputField,
                    customClass?.InputField
                )}
            >
                {input.icon && (
                    <div className={customClass?.Icon}>{input.icon}</div>
                )}
                <input
                    type={isSearchType ? 'text' : input.type}
                    className={classNames(
                        {
                            [styles.Error]: hasError,
                            [styles.WithClear]: clearVisible,
                        },
                        customClass?.Input
                    )}
                    id={input.id}
                    value={input.value}
                    disabled={isDisabled}
                    onChange={updateInputValue}
                    onFocus={showClear}
                    placeholder={input.placeholder}
                    onKeyUp={onKeyUp}
                    autoComplete={input.autoComplete}
                    maxLength={input.maxLength || 524288}
                />
                {withClearButton && (
                    <button
                        className={classNames(styles.Clear, {
                            [styles.Search]: isSearchType,
                            [styles.Visible]: clearVisible,
                        })}
                        onClick={clearInput}
                    />
                )}
            </div>
            {!!errorText && <div className={styles.ErrorText}>{errorText}</div>}
        </div>
    );
};

export default Input;
